<template>
    <Card :title="$t('pages.MainInformation')">
        <div class="card-body">
            <div class="row">
                <div class="col-lg-2">
                    <ProductImageCard
                        :image="mainImage"
                        @update:image="updateMainImage"
                        type="product-item"
                    />
                </div>
                <div class="col-lg-10">
                    <div class="row">
                        <div class="col-lg-6">
                            <FormGroup :label="$t('labels.name')" :required="true">
                                <Input
                                    :model-value="product?.name"
                                    @update:model-value="product.name = $event"
                                    :placeholder="$t('labels.name')"
                                />
                            </FormGroup>
                        </div>
                        <div class="col-lg-2">
                            <FormGroup :label="$t('labels.sku')">
                                <Input
                                    :model-value="product?.sku"
                                    @update:model-value="product.sku = $event"
                                    :placeholder="$t('labels.sku')"
                                />
                            </FormGroup>
                        </div>
                        <div class="col-lg-2">
                            <FormGroup :label="$t('labels.product_type')">
                                <Autocomplete
                                    :url="`/projects/${$route.params.projectUuid}/pim/types`"
                                    :model-value="product?.category?.uuid"
                                    :selected-value="product?.category?.name"
                                    :can-be-empty="true"
                                    @update:model-value="product.category = $event"
                                />
                            </FormGroup>
                        </div>
                        <div class="col-lg-2">
                            <FormGroup :label="$t('labels.measurement')">
                                <Select
                                    :model-value="product?.measurement"
                                    @update:model-value="product.measurement = $event !== null ? $event.value : null"
                                    :selected-value="measurementOptions.find(item => item.value === product?.measurement)?.label"
                                    :options="measurementOptions"
                                    option-key="value"
                                    option-value="label"
                                />
                            </FormGroup>
                        </div>
                        <div class="col-lg-3">
                            <FormGroup :label="$t('labels.manufacturer')">
                                <Input
                                    :model-value="product?.manufacturer"
                                    @update:model-value="product.manufacturer = $event"
                                />
                            </FormGroup>
                        </div>
                        <div class="col-lg-3">
                            <FormGroup :label="$t('labels.group_key')">
                                <Input
                                    :model-value="product?.group_key"
                                    @update:model-value="product.group_key = $event"
                                />
                            </FormGroup>
                        </div>
                        <div class="col-lg-2">
                            <FormGroup :label="$t('labels.cost')">
                                <Input
                                    :model-value="product?.cost"
                                    @update:model-value="(newCost) => { product.cost = newCost, setDefaultCurrency() }"
                                    type="number"
                                />
                            </FormGroup>
                        </div>
                        <div class="col-lg-2">
                            <FormGroup :label="$t('labels.cost_currency')">
                                <Select
                                    :model-value="product?.cost_currency?.uuid"
                                    :selected-value="product?.cost_currency?.name"
                                    :options="currencies"
                                    @update:model-value="product.cost_currency = $event"
                                    option-key="uuid"
                                    option-value="name"
                                />
                            </FormGroup>
                        </div>
                        <div class="col-lg-2">
                            <FormGroup :label="$t('labels.automatic_arrival')">
                                <CheckBox
                                    :model-value="product?.automatic_arrival"
                                    @update:model-value="product.automatic_arrival = $event"
                                />
                            </FormGroup>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="card-footer d-flex justify-content-end flex-wrap" :style="{ gap: '0.5rem' }">
            <AppButton color="light" @click="$emit('duplicate')">
                <i class="icon-copy4 mr-2"></i>
                {{ $t('buttons.duplicate') }}
            </AppButton>
            <AppButton color="light" @click="$emit('delete')">
                <i class="icon-trash mr-2"></i>
                {{ $t('buttons.delete') }}
            </AppButton>
            <AppButton color="indigo" @click="$emit('update')">
                <i class="icon-floppy-disk mr-2"></i>
                {{ $t('buttons.save') }}
            </AppButton>
        </div>
    </Card>
</template>
<script>
import Card from "../../../../../components/new/CardComponent.vue";
import FormGroup from "../../../../../components/new/form/group/FormGroup.vue";
import Input from "../../../../../components/new/form/Input.vue";
import InputGroup from "../../../../../components/new/form/InputGroup.vue";
import Autocomplete from "../../../../../components/new/form/autocomplete/AppAutoComplete.vue";
import LinkButton from "../../../../../components/new/button/LinkButton.vue";
import CheckBox from "../../../../../components/new/form/checkbox/AppCheckbox.vue";
import Select from "../../../../../components/new/form/select/AppSelect.vue";
import ProductImageCard from "../components/ProductImageCard.vue";
import AppButton from "../../../../../components/new/button/AppButton.vue";

import { mapGetters } from "vuex";
import { httpClient } from "../../../../../services/HttpClient";
import MeasurementEnum from "../../../../enums/MeasurementEnum";

export default {
    name: "InternalProductMainInformationPage",
    components: {
        Card, FormGroup, Input, InputGroup, Autocomplete, LinkButton, CheckBox, Select, ProductImageCard,
        AppButton
    },
    props: {
        product: {
            type: [ Object, null ],
            required: true
        },
        relations: {
            type: Array,
            required: true
        }
    },
    emits: ["update", "duplicate", "delete"],
    data() {
        return {
            measurementOptions: [],
            currencies: [],
            newMainImage: null,
            mainImageRemoved: false
        }
    },
    methods: {
        updateMainImage(image) {
            this.newMainImage = image;
            this.mainImageRemoved = image === null;
        },
        fetchCurrencies() {
            httpClient.get('/list/currencies', {
                limit: 100
            }).then((response) => {
                this.currencies = response.data.data;
            })
        },
        setDefaultCurrency() {
            if (this.currencies.length > 1) {
                return;
            }

            this.product.cost_currency = this.currencies[0];
        }
    },
    computed: {
        ...mapGetters('route', ['project']),
        mainImage() {
            if (this.mainImageRemoved) {
                return null;
            }

            return this.newMainImage || { url: this.product?.main_image_url };
        }
    },
    mounted() {
        this.fetchCurrencies();
        this.measurementOptions = Object.values(MeasurementEnum).map((value) => {
            return {
                value,
                label: this.$t(`measurement.${value}`)
            }
        })
    }
}
</script>
